import { computed, ComputedRef, Ref } from 'vue';
import { IStatGroup } from '@/interfaces/IStatGroup';
import { i18n } from '@/plugins/i18n';

interface ISeriesStatsLike {
  groups: IStatGroup[];
}

/**
 * Derives a display copy of a stats payload with translated series titles
 * (looked up by group id), currency suffixes and palette colors.
 *
 * Titles are resolved in a computed instead of being baked into the fetched
 * data so a runtime language switch re-labels toggles, chart legends, table
 * headers and exports without refetching.
 */
export function useStatsSeriesDisplay<TStats extends ISeriesStatsLike>(options: {
  stats: Ref<TStats | null>;
  /** Maps raw group ids (as returned by the API) to i18n keys. */
  labelKeys: Record<string, string>;
  colors: string[];
  currencySymbol: Ref<string>;
}): ComputedRef<TStats | null> {
  return computed<TStats | null>(() => {
    const stats = options.stats.value;
    if (!stats) return null;

    const groups = stats.groups.map((group, index) => {
      const key = options.labelKeys[group.id];
      const title =
        (key ? String(i18n.t(key)) : group.title) +
        (group.type === 'currency' ? ` (${options.currencySymbol.value})` : '');
      return {
        ...group,
        title,
        color: options.colors[index % options.colors.length],
      };
    });

    return { ...stats, groups };
  });
}
